import React from "react";
import { pronounForms } from "../../lib/sections.js";

const SUBTYPE_LABELS = {
  // sensory
  love:             "Loves",
  comfort:          "Comfort",
  distress:         "Distress",
  // communication
  // yes / no / done take the person's pronouns — see subtypeLabel (Oct 25).
  overwhelm_early:  "Early overwhelm signs",
  phrase:           "Key phrases",
  aac:              "AAC / tablet",
  // support
  early_sign:       "Early signs",
  trigger:          "Triggers",
  what_helps:       "What helps",
  what_makes_worse: "What makes it worse",
  after_care:       "After care",
};

const SENSE_LABELS = {
  sight: "👁 Sight",
  sound: "👂 Sound",
  touch: "✋ Touch",
  taste: "👅 Taste",
  smell: "👃 Smell",
  interoception: "💓 Interoception",
  other: "Other",
};

function groupBySubtype(items) {
  const groups = {};
  for (const item of items) {
    const key = item.subtype;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}

function ItemCard({ item }) {
  return (
    <div style={{ ...styles.card, ...(item.critical ? styles.critical : {}) }}>
      {item.critical && <span style={styles.criticalBadge}>Critical</span>}
      <h4 style={styles.cardTitle}>{item.title}</h4>
      <p style={styles.cardDesc}>{item.description}</p>
      {item.example && (
        <p style={styles.example}>
          <span style={styles.exampleLabel}>Example: </span>
          {item.example}
        </p>
      )}
      {item.sense && SENSE_LABELS[item.sense] && (
        <span style={styles.senseBadge}>{SENSE_LABELS[item.sense]}</span>
      )}
    </div>
  );
}

/** "How he says yes" / "How they say yes" — was hardwired "How she …". */
export function subtypeLabel(subtype, pronouns = "") {
  if (subtype === "yes" || subtype === "no" || subtype === "done") {
    const f = pronounForms(pronouns);
    return `How ${f.subj} ${f.plural ? "say" : "says"} ${subtype}`;
  }
  return SUBTYPE_LABELS[subtype] || subtype.replace(/_/g, " ");
}

export default function ItemList({ items = [], pronouns = "" }) {
  if (!items.length) return null;

  // Critical items first within each group
  const sorted = [...items].sort((a, b) => Number(b.critical) - Number(a.critical) || a.order - b.order);
  const groups = groupBySubtype(sorted);

  return (
    <div style={styles.root}>
      {Object.entries(groups).map(([subtype, groupItems]) => (
        <div key={subtype} style={styles.group}>
          <h3 style={styles.groupLabel}>
            {subtypeLabel(subtype, pronouns)}
          </h3>
          <div style={styles.cards}>
            {groupItems.map(item => <ItemCard key={item.id} item={item} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: "var(--space-8)" },
  group: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  groupLabel: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "var(--color-text-tertiary)",
    paddingBottom: "var(--space-2)",
    borderBottom: "1px solid var(--color-border)",
  },
  cards: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  card: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-5)",
    position: "relative",
  },
  critical: {
    background: "var(--color-critical-bg)",
    borderColor: "var(--color-critical)",
  },
  criticalBadge: {
    display: "inline-block",
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-critical)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: "var(--space-2)",
  },
  cardTitle: {
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-primary)",
    marginBottom: "var(--space-2)",
  },
  cardDesc: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
  },
  example: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
    marginTop: "var(--space-3)",
    fontStyle: "italic",
  },
  exampleLabel: {
    fontStyle: "normal",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-secondary)",
  },
  senseBadge: {
    display: "inline-block",
    marginTop: "var(--space-3)",
    fontSize: "var(--text-xs)",
    color: "var(--color-text-tertiary)",
    background: "var(--color-bg-subtle)",
    borderRadius: "var(--radius-full)",
    padding: "2px var(--space-2)",
  },
};
